import { getApiBaseUrl } from "@/config/runtime";
import type {
  CollectionFileSummary,
  DataCollection,
  DataCollectionUpdatePayload,
  DataDocument,
  DataResponse,
  PaginatedResponse,
  VisibilityImpactResponse,
} from "@/types";
import { api, getHeaders } from "../api";

export const dataSourceService = {
  list: (params?: { page?: number; pageSize?: number; visibility?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    if (params?.visibility) searchParams.set("visibility", params.visibility);
    return api.get<PaginatedResponse<DataCollection>>(`/data-sources?${searchParams}`);
  },
  get: (collectionId: string) => api.get<DataResponse<DataCollection>>(`/data-sources/${collectionId}`),
  create: (data: Partial<DataCollection>) => api.post<DataResponse<DataCollection>>("/data-sources", data),
  update: (collectionId: string, data: DataCollectionUpdatePayload) =>
    api.put<DataResponse<DataCollection>>(`/data-sources/${collectionId}`, data),
  delete: (collectionId: string) => api.delete(`/data-sources/${collectionId}`),
  sync: (collectionId: string) => api.post(`/data-sources/${collectionId}/sync`, {}),
  getDocuments: (collectionId: string) =>
    api.get<DataResponse<DataDocument[]>>(`/data-sources/${collectionId}/documents`),
  uploadDocuments: (collectionId: string, files: File[]) =>
    api.upload<DataResponse<DataDocument[]>>(`/data-sources/${collectionId}/files`, files),
  deleteDocument: (collectionId: string, documentId: string) =>
    api.delete(`/data-sources/${collectionId}/files/${documentId}`),
  getFileDownloadUrl: (collectionId: string, fileId: string, expiryHours?: number) => {
    const params = expiryHours ? `?expiry_hours=${expiryHours}` : "";
    return api.get<DataResponse<{ url: string; filename: string; expires_in_hours: number }>>(
      `/data-sources/${collectionId}/files/${fileId}/download${params}`,
    );
  },

  // Download file as blob for local storage
  downloadFileBlob: async (collectionId: string, fileId: string): Promise<Blob> => {
    const headers = await getHeaders();
    const response = await fetch(`${getApiBaseUrl()}/data-sources/${collectionId}/files/${fileId}/stream`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    return response.blob();
  },

  // Download the AI-ready markdown version of a file
  downloadMarkdownBlob: async (collectionId: string, fileId: string): Promise<Blob> => {
    const headers = await getHeaders();
    const response = await fetch(`${getApiBaseUrl()}/data-sources/${collectionId}/files/${fileId}/markdown`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    return response.blob();
  },

  // Notes CRUD
  addNote: (collectionId: string, data: { title: string; content: string }) =>
    api.post<DataResponse<CollectionFileSummary>>(`/data-sources/${collectionId}/notes`, data),

  // Website URL management
  addWebsiteUrl: (collectionId: string, url: string) =>
    api.post<DataResponse<CollectionFileSummary>>(`/data-sources/${collectionId}/urls`, { url }),
  updateNote: (collectionId: string, fileId: string, data: { title?: string; content?: string }) =>
    api.put<DataResponse<CollectionFileSummary>>(`/data-sources/${collectionId}/notes/${fileId}`, data),
  getNoteContent: (collectionId: string, fileId: string) =>
    api.get<DataResponse<{ uuid: string; title: string; content: string; status: string; created_at: string }>>(
      `/data-sources/${collectionId}/notes/${fileId}`,
    ),

  // Visibility impact preview
  getVisibilityImpact: (collectionId: string, visibility: string, teamUuid?: string) => {
    const params = new URLSearchParams({ visibility });
    if (teamUuid) params.set("team_uuid", teamUuid);
    return api.get<DataResponse<VisibilityImpactResponse>>(`/data-sources/${collectionId}/visibility-impact?${params}`);
  },
};
